import { ReactNode } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useAccount } from '@hcs/authn';
import { ActionButtons, Dialog, Input, TextButton } from '@hcs/design-system';
import { useActiveState } from '@hcs/hooks';
import { useOrgIsSsoOnly } from '@hcs/single-sign-on';

import {
  UpdateEmailRequest,
  useUpdateUserEmail,
} from '../../hooks/useUpdateUserEmail';
const yupSchema = yup.object().shape({
  email: yup.string().email().required('Email is required'),
});

interface ChangeUserEmailProps {
  onSubmit?: (email: string) => void;
  onSuccess?: (data: void, updateEmailRequest: UpdateEmailRequest) => void;
  onCancel?: VoidFunction;
  actionPortalIdRender?: string;
  confirmationUrl: string;
}

const dataHcName = 'change-user-email';
export const ChangeUserEmail = ({
  onSubmit,
  onSuccess,
  onCancel,
  actionPortalIdRender,
  confirmationUrl,
}: ChangeUserEmailProps) => {
  const { data: account } = useAccount();
  const { isError: isNotSso } = useOrgIsSsoOnly(account?.organization.id);
  const { isPending, mutate: updateUserEmail } = useUpdateUserEmail({
    onSuccess,
  });
  const form = useForm<{ email: string }>({
    resolver: yupResolver(yupSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const userId = account?.user.id;
  if (!isNotSso) {
    return null;
  }
  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit((v) => {
          if (userId) {
            updateUserEmail({
              userId,
              email: v.email,
              update_email_url: confirmationUrl,
            });
            onSubmit?.(v.email);
          }
        })}
      >
        <p>
          After changing your email address a confirmation email will be sent to
          the new email address. You will be required to confirm your new email
          address before using it to log in.
        </p>
        <Controller
          name="email"
          control={form.control}
          render={({ field }) => {
            return (
              <Input
                {...field}
                value={field.value || ''}
                dataHcName={`${dataHcName}-${field.name}`}
                error={form.formState.errors[field.name]?.message}
              />
            );
          }}
        />
        <br />
        <ActionButtons
          actions={[
            {
              label: 'Cancel',
              dataHcName: `${dataHcName}-cancel`,
              secondary: true,
              type: 'button',
              onClick: () => {
                form.reset();
                onCancel?.();
              },
            },
            {
              label: 'Submit',
              type: 'submit',
              dataHcName: `${dataHcName}-submit`,
              disabled:
                isPending || !form.formState.isDirty || !form.formState.isValid,
              onClick: () => {
                onSubmit?.(form.getValues('email'));
              },
            },
          ]}
          portalIdRender={actionPortalIdRender}
        />
      </form>
    </FormProvider>
  );
};

export const ChangeUserEmailLauncher = ({
  trigger,
  onCancel,
  onSuccess,
  ...props
}: ChangeUserEmailProps & { trigger?: ReactNode }) => {
  const { data: account } = useAccount();
  const { isError: isNotSso } = useOrgIsSsoOnly(account?.organization.id);
  const { active, handleClose, handleOpen } = useActiveState();
  if (!isNotSso) {
    return null;
  }
  return (
    <>
      {trigger ? (
        <span
          data-hc-name={`${dataHcName}-launcher-trigger`}
          onClick={handleOpen}
        >
          {trigger}
        </span>
      ) : (
        <TextButton
          dataHcName={`${dataHcName}-launcher-trigger`}
          onClick={handleOpen}
        >
          Change Email
        </TextButton>
      )}
      <Dialog
        title="Change Email"
        dataHcName={`${dataHcName}-dialog`}
        active={active}
        onClose={handleClose}
      >
        <ChangeUserEmail
          {...props}
          onSuccess={(data, variables) => {
            handleClose();
            onSuccess?.(data, variables);
          }}
          onCancel={() => {
            handleClose();
            onCancel?.();
          }}
        />
      </Dialog>
    </>
  );
};
